<template>
  <single-selector-template
    ref="modalRef"
    :title="finalTitle"
    :primary-color="finalPrimaryColor"
    :selected-item="localSelectedUser"
    :clearable="finalClearable"
    :clear-text="standardNaming.CLEAR_TEXT"
    :clear-aria-label="'Clear user selection'"
    :show-find="true"
    :auto-focus-find="true"
    :show-archived-toggle="finalShowArchivedToggle"
    :include-archived="includeInactiveUsers"
    :show-none-option="currentNoItemOption"
    :none-selected="localHasNoUserSelected"
    :show-row2="true"
    :show-footer-controls="finalShowArchivedToggle || currentNoItemOption"
    :show-footer="
      !currentCloseOnSelect || finalShowArchivedToggle || currentNoItemOption
    "
    :primary-action-text="finalActionText"
    :secondary-action-text="withShortcut('Cancel', shortcuts.CANCEL)"
    :loading="isLoading"
    :close-on-select="currentCloseOnSelect"
    width="medium"
    @show="onShow"
    @hide="onHide"
    @shown="onShown"
    @clear="clear"
    @search="handleSearch"
    @save="handleSave"
    @cancel="handleCancel"
    @item-selected="handleUserSelected"
    @focus-first="handleFocusFirst"
    @focus-last="handleFocusLast"
    @focus-body-last="handleFocusBodyLast"
    @toggle-archived="(value: boolean) => handleToggleArchived(value)"
    @select-none="handleSelectNone"
  >
    <user-browser
      ref="userBrowserRef"
      mode="single"
      :selected-item="localSelectedUser"
      :primary-color="finalPrimaryColor"
      :include-archived="includeInactiveUsers"
      :hide-footer="true"
      :team-ids="teamIds"
      @item-selected="handleUserSelected"
    />
  </single-selector-template>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });
import {
  OrganizationMembership,
  OrganizationMembershipQuery,
} from '@/models/user.models';
import { Colors } from '@/api';
import { ref, nextTick, computed, provide, toRef, Ref } from 'vue';
import { useKeyboardShortcuts } from '@/composables/modal/useKeyboardShortcuts';
import { organizationData } from '@/store/helpers';
import UserService from '@/services/domain/userService';
import UserBrowser from './UserBrowser.vue';
import SingleSelectorTemplate from '../../templates/SingleSelectorTemplate.vue';
import { STANDARD_NAMING } from '../../types/selectorStandards';
import type {
  StandardSingleSelectorProps,
  StandardSingleSelectorEmits,
  StandardBrowserInstance,
  StandardSelectorInstance,
} from '../../types/selectorStandards';
import {
  useBaseSelector,
  useSaveHandler,
} from '../../composables/useBaseSelector';
import {
  useEnhancedModalLifecycle,
  type BrowserInstance,
} from '../../composables/useBrowserInitialization';

// Import standard naming constants
const standardNaming = STANDARD_NAMING;

// Type for UserBrowser - extends standard interface
interface UserBrowserInstance
  extends StandardBrowserInstance<OrganizationMembership> {
  getFocusedUser: () => OrganizationMembership | 'no-user' | null;
}

/**
 * Props Interface
 * Following the new standard: accept only string IDs, load objects internally
 */
interface Props extends StandardSingleSelectorProps<OrganizationMembership> {
  // User-specific props
  showArchivedToggle?: boolean;
  teamIds?: string[];
  userQuery?: OrganizationMembershipQuery;
  // When true, clearing will emit selected(null) and close the modal
  closeOnClear?: boolean;
}

/**
 * Emits Interface
 * Using standard event contract
 */
type Emits = StandardSingleSelectorEmits<OrganizationMembership>;

const props = withDefaults(defineProps<Props>(), {
  primaryColor: Colors.BLUE,
  title: 'Select a User',
  clearable: true,
  actionText: 'Select',
  noItemOption: false,
  noItemText: 'No User',
  closeOnSelect: true, // Default to true for single select
  showArchivedToggle: true,
  closeOnClear: false,
});

const emit = defineEmits<Emits>();
const { shortcuts, withShortcut } = useKeyboardShortcuts();

// Refs
const modalRef = ref<StandardSelectorInstance>();
const userBrowserRef = ref<UserBrowserInstance>();

// Use shared composable for base selector logic
const { orgMembershipCache } = organizationData();
const {
  localSelected,
  hasNoItemSelected: localHasNoUserSelected,
  isLoading,
  loadFromIds,
  clearSelection,
} = useBaseSelector<OrganizationMembership>({
  value: toRef(props, 'value'),
  cache: orgMembershipCache,
  getItemId: UserService.getItemId,
  findInCache: UserService.findInCache,
  isMulti: false,
});

// Cast to proper type for single-select
const localSelectedUser = localSelected as Ref<OrganizationMembership | null>;

// Provide keyboard helpers for BaseModal
provide('keyboardHelpers', {
  getSearchResultsCount: () => {
    return userBrowserRef.value?.getSearchResultsCount?.() || 0;
  },
});

// State
const searchQuery = ref('');
const includeInactiveUsers = ref(false);

// Computed values
const currentActionText = computed(() => props.actionText ?? 'Select');
const currentNoItemOption = computed(() => props.noItemOption ?? false);
const currentCloseOnSelect = computed(() => props.closeOnSelect ?? true);
const finalShowArchivedToggle = computed(
  () => props.showArchivedToggle ?? true
);

// Final computed values for the template
const finalTitle = computed(() => props.title || 'Select a User');
const finalPrimaryColor = computed(() => props.primaryColor || Colors.BLUE);
const finalClearable = computed(() => props.clearable ?? true);
const finalActionText = computed(() => {
  if (currentCloseOnSelect.value) {
    return ''; // No footer shown when closeOnSelect is true
  }
  const actionText = currentActionText.value;
  return withShortcut(actionText, shortcuts.SAVE);
});

// Use enhanced modal lifecycle with integrated browser initialization
const { onShow, onHide, onShown } = useEnhancedModalLifecycle({
  browserOptions: {
    browserRef: userBrowserRef as Ref<BrowserInstance | undefined>,
    getInitParams: () => undefined, // UserBrowser doesn't take params
    onInitialized: async () => {
      // Scroll to selected item after initialization
      await nextTick();
      userBrowserRef.value?.scrollToFirstSelected?.();
    },
    onError: () => {
      // Error handled silently - browser initialization failed
    },
  },
  loadData: loadFromIds,
  emit: emit as (event: string, ...args: unknown[]) => void,
});

// Use shared save handler composable
const {
  handleSave: originalHandleSave,
  handleCancel: originalHandleCancel,
  handleItemSelected: originalHandleUserSelected,
  handleNoItemSelected: originalHandleNoUserSelected,
} = useSaveHandler({
  localSelected: localSelectedUser,
  hasNoItemSelected: localHasNoUserSelected,
  closeOnSelect: currentCloseOnSelect,
  closeModal: () => modalRef.value?.closePopup(),
  emit: emit as (event: string, ...args: unknown[]) => void,
  isMulti: false,
  getFocusedItem: () => userBrowserRef.value?.getFocusedUser() || null,
});

// Wrap handlers
const handleSave = () => {
  originalHandleSave();
};

const handleCancel = () => {
  originalHandleCancel();
};

const handleUserSelected = (user: OrganizationMembership | null) => {
  if (user) {
    originalHandleUserSelected(user);
  } else {
    handleNoUserSelected();
  }
};

const handleNoUserSelected = () => {
  originalHandleNoUserSelected();
};

// Search handling
function handleSearch(query: string) {
  searchQuery.value = query;
  userBrowserRef.value?.setSearchQuery(query);
}

// Template-level handlers for keyboard shortcuts
function handleToggleArchived(value: boolean) {
  includeInactiveUsers.value = value;
}

function handleSelectNone() {
  if (currentNoItemOption.value) {
    handleNoUserSelected();
  }
}

// Navigation handlers removed - BaseModal handles via DOM manipulation

function clear() {
  clearSelection();
  emit('update:value', null);
  emit('cleared');

  // When closeOnClear is true, also emit selected and close the modal
  if (props.closeOnClear) {
    // Emit selected with undefined to trigger the handler (typed as optional param)
    emit('selected', undefined as unknown as OrganizationMembership);
    modalRef.value?.closePopup();
  }
}

// Focus handling
function handleFocusFirst() {
  userBrowserRef.value?.handleFocusFirst();
}

function handleFocusLast() {
  userBrowserRef.value?.handleFocusLast();
}

async function handleFocusBodyLast() {
  await nextTick();
  userBrowserRef.value?.handleFocusLastFromFooter?.() ||
    userBrowserRef.value?.handleFocusLast();
}

// Public API
function showPopup(trigger?: HTMLElement) {
  modalRef.value?.showPopup(trigger);
}

function closePopup() {
  modalRef.value?.closePopup();
}

defineExpose({
  showPopup,
  closePopup,
  isVisible: computed(() => modalRef.value?.isVisible || false),
});
</script>
